import { set , get, del } from 'idb-keyval'
import Swiper from 'swiper';
import 'swiper/swiper-bundle.css';
/**
 * Customer Reviews Component
 * Fetches reviews from Yotpo API and displays them with custom pagination
 * Reference: https://apidocs.yotpo.com/reference/retrieve-reviews-for-a-product
 */

const reviewsState = {};
const CACHE_TTL_24HR_MS = 24 * 60 * 60 * 1000;
const FADE_MS = 150;
const HEIGHT_MS = 250;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Animate the grid from its old height to its new natural height.
 * Uses explicit `height` so it can shrink as well as grow.
 */
async function animateGridHeight(grid, prevHeight, isInitial) {
  grid.style.height = '';                 // let it size naturally
  const nextHeight = grid.offsetHeight;   // measure (forces layout)

  if (isInitial || Math.abs(prevHeight - nextHeight) < 2) return;

  grid.style.height = `${prevHeight}px`;  // snap back to the old height
  void grid.offsetHeight;                 // force reflow so the transition starts from here
  grid.style.height = `${nextHeight}px`;  // animate to the new one

  await wait(HEIGHT_MS + 30);
  grid.style.height = '';                 // return to auto
}

function initializeAllCustomerReviewsSections() {
  const sections = document.querySelectorAll('[data-component="customer-reviews"]');


  sections.forEach(section => {
    const sectionId = section.getAttribute('data-section-id');
    const grid = document.getElementById(`reviews-grid-${sectionId}`);

    if (grid) {
      const productId = grid.getAttribute('data-product-id');
      const appKey = grid.getAttribute('data-app-key');
      const reviewsPerPage = parseInt(grid.getAttribute('data-reviews-per-page')) || 4;
      const raw = grid.dataset.visible_review_ids || '';

      // Restore last viewed page from session storage
      const savedPage = parseInt(sessionStorage.getItem(`akc-reviews-page-${sectionId}`)) || 1;

      // Initialize state for this section
      reviewsState[sectionId] = {
        productId,
        appKey,
        reviewsPerPage,
        currentPage: savedPage,
        totalPages: 1,
        reviews: [],
        loading: false,
        visibleReviewIds: (raw)
          .split(',')
          .map(id => parseInt(id.trim()))
          .filter(Boolean)
      };

      // Debug logging
      if (window.akc?.debug) {
        console.log('Customer Reviews initialized:', {
          sectionId,
          productId,
          appKey,
          reviewsPerPage,
          savedPage
        });
      }

      // Fetch and display reviews
      fetchAndDisplayReviews(sectionId, savedPage);
    }
  });
}

/**
 * Fetch all reviews from Yotpo API, of a product by iterating through all pages
 * @param {Object} state - State object containing product and app key info
 */
const PER_PAGE = 150; // confirmed v3 maximum
async function fetchAllReviews(state) {
  const base = `https://api-cdn.yotpo.com/v3/storefront/store/${encodeURIComponent(state.appKey)}/product/${encodeURIComponent(state.productId)}/reviews`;

  const fetchPage = async page => {
    const params = new URLSearchParams({
      page,
      perPage: PER_PAGE,
      sort: 'date',
      direction: 'desc'
    });
    const response = await fetch(`${base}?${params}`);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  };

  // Page 1 tells us the total, so we know how many more pages to request
  const first = await fetchPage(1);
  const firstReviews = first.reviews || [];
  const total = first.pagination?.total ?? firstReviews.length;
  const perPage = first.pagination?.perPage || firstReviews.length;

  if (firstReviews.length >= total || perPage <= 0) return firstReviews;

  // Remaining pages in parallel (e.g. pages 2-4 for 502 reviews)
  const totalPages = Math.ceil(total / perPage);
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2))
  );

  // Promise.all keeps order, so reviews stay sorted newest first
  return firstReviews.concat(...rest.map(d => d.reviews || []));
}

/**
 * Fetch reviews from Yotpo API
 * @param {string} sectionId - Section ID
 * @param {number} page - Page number (1-indexed)
 */
async function fetchAndDisplayReviews(sectionId, page = 1) {
  const state = reviewsState[sectionId];
  if (!state || state.loading) return;

  const grid = document.getElementById(`reviews-grid-${sectionId}`);

  if (!state.productId || !state.appKey) {
    grid.innerHTML = '<div class="customer-reviews__error">Unable to load reviews. Please make sure a product is selected and the Yotpo App Key is configured.</div>';
    return;
  }

  state.loading = true;

  try {
    // Fetch all reviews once, cache the full set
    const cacheKey = `akc-reviews-v3-${state.productId}`;

    // Read from IndexedDB and check expiration
    const cachedRecord = await get(cacheKey);
    let allReviews = null;

    if (cachedRecord) {
      // Check if the 24-hour window has passed
      if (Date.now() > cachedRecord.expiresAt) {
        if (window.akc?.debug) console.log('Cache expired — purging database record asynchronously');
        await del(cacheKey); // Lazy background purge
      } else {
        if (window.akc?.debug) console.log('Cache hit:', cacheKey);
        allReviews = cachedRecord.data;
      }
    }

    // Cache Miss or Expired Fallback
    if (!allReviews) {
      if (window.akc?.debug) console.log('Cache miss — fetching all reviews from network');
      allReviews = await fetchAllReviews(state);

      // Save object array with expiration metadata natively without stringifying
      await set(cacheKey, {
        data: allReviews,
        expiresAt: Date.now() + CACHE_TTL_24HR_MS
      });
    }

    // ── TEMP DEV ONLY — inject test media onto first review ──────────
    if (window.akc?.debug && allReviews.length > 0) {
      const mockImg = id => ({
        id,
        thumbUrl: `https://picsum.photos/id/${id}/282/282`,
        originalUrl: `https://picsum.photos/id/${id}/1200/1200`
      });
      allReviews[0].imagesData = [10, 20, 30, 31, 32, 33, 34, 35, 36, 37].map(mockImg);
      allReviews[0].videosData = [
        { id: 1, videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4', thumbnailUrl: 'https://picsum.photos/id/40/282/282', duration: 10.0 },
        { id: 2, videoUrl: 'https://www.w3schools.com/html/movie.mp4',   thumbnailUrl: 'https://picsum.photos/id/50/282/282', duration: 10.0 }
      ];
    }
    // ── END TEMP ─────────────────────────────────────────────────────

    // Filter reviews
    const visibleReviews = window.akc?.designMode
      ? allReviews
      : allReviews.filter(r => state.visibleReviewIds.includes(r.id));

    // Client-side pagination
    const start = (page - 1) * state.reviewsPerPage;
    const pageReviews = visibleReviews.slice(start, start + state.reviewsPerPage);

    state.currentPage = page;
    state.totalPages = Math.ceil(visibleReviews.length / state.reviewsPerPage) || 1;
    state.reviews = pageReviews;

    const isInitial = grid.hasAttribute('data-loading');
    const prevHeight = grid.offsetHeight;

    // 1. Fade out the old page (skip on first load)
    if (!isInitial) {
      grid.style.height = `${prevHeight}px`;
      grid.classList.add('is-transitioning');
      await wait(FADE_MS);
    }

    // 2. Swap content + measure while invisible
    grid.removeAttribute('data-loading');
    renderReviews(sectionId, pageReviews);
    applyRowSpans(sectionId);        // now synchronous, spans are set before anything is visible
    initMediaCarousels(sectionId);
    renderPagination(sectionId);
    initDesignModeCheckboxes(sectionId);

    // 3. Animate to the new height, then fade in
    await animateGridHeight(grid, prevHeight, isInitial);
    grid.classList.remove('is-transitioning');

  } catch (error) {
    console.warn('Failed to fetch reviews:', error);
    if (grid) {
      grid.classList.remove('is-transitioning');
      grid.style.height = '';
      grid.removeAttribute('data-loading');
      grid.innerHTML = '<div class="customer-reviews__error">Failed to load reviews. Please try again later.</div>';
    }
  } finally {
    state.loading = false;
  }
}

/**
 * Render review cards
 * @param {string} sectionId - Section ID
 * @param {Array} reviews - Array of review objects
 */
function renderReviews(sectionId, reviews) {
  const grid = document.getElementById(`reviews-grid-${sectionId}`);
  if (!grid) return;


  if (reviews.length === 0) {
    grid.innerHTML = '<div class="customer-reviews__no-reviews">No reviews yet.</div>';
    return;
  }

  function getVideoUrl(vid) {
    return vid.videoUrl || null; // videoUrl only exists in the dev mock
  }

  const reviewsHTML = reviews.map((review, idx) => {
    const score = review.score || 0;
    const reviewerName = review.user?.displayName || 'Anonymous';
    const reviewerImage = review.user?.socialImage || '';
    const reviewDate = formatDate(review.createdAt);
    const reviewText = review.content || '';
    const reviewTitle = review.title || '';

    let reviewImages = (review.imagesData || [])
      .map(img => ({
        type: 'image',
        thumb: img.thumbUrl,
        full: img.originalUrl || img.thumbUrl
      }))
      .filter(m => m.thumb || m.full);

    const reviewVideos = (review.videosData || [])
      .map(vid => ({
        type: 'video',
        url: getVideoUrl(vid),
        thumb: vid.thumbnailUrl,
        duration: vid.duration || null
      }))
      .filter(v => v.url);

    const media = [...reviewImages, ...reviewVideos];

    // Debug-only placeholder to preview image layout
    if (window.akc?.debug && idx === 0 && reviewImages.length === 0) {
      reviewImages = ['https://placehold.co/282x282'];
    }

    // Debug first review to see all available fields
    if (window.akc?.debug && idx === 0) {
      console.log('First review object:', review);
      console.log('Raw images_data:', review.images_data);
      console.log('Raw videos_data:', review.videos_data);
      console.log('Extracted media:', media);
    }

    const stars = generateStars(score);

    const mediaHTML = media.length > 0 ? `
      <div class="customer-reviews__card-media swiper-container">
        <div class="customer-reviews__media-track swiper-wrapper">
          ${media.map((item, i) => `
            <div class="customer-reviews__media-slide swiper-slide">
              ${item.type === 'image'
                ? `<a href="${item.full}" class="customer-reviews__media-link" target="_blank" rel="noopener">
                    <img
                      ${i <= 1 ? `src="${item.full}"` : `data-src="${item.full}" src="${item.thumb}"`}
                      alt="Review image ${i + 1}"
                      class="customer-reviews__media-thumb swiper-lazy">
                  </a>`
                : `<div class="customer-reviews__media-item--video">
                    <video
                      src="${item.url}"
                      ${item.thumb ? `poster="${item.thumb}"` : ''}
                      preload="none"
                      playsinline
                      class="customer-reviews__media-thumb">
                    </video>
                    <button class="customer-reviews__video-play" aria-label="Play video"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M8 5v14l11-7z"/></svg></button>
                    ${item.duration ? `<span class="customer-reviews__media-duration">${formatDuration(item.duration)}</span>` : ''}
                  </div>`
              }
            </div>
          `).join('')}
        </div>
        ${media.length > 1 ? `
          <div class="swiper-pagination"></div>
        ` : ''}
      </div>
    ` : '';

    return `
      <div class="customer-reviews__card" data-review-id="${review.id}">
        ${window.akc?.designMode ? `
          <div class="customer-reviews__card-id">
            <input
              type="checkbox"
              id="review-id-${review.id}"
              name="review-id-${review.id}"
              class="customer-reviews__card-checkbox"
              data-section-id="${sectionId}"
              data-review-id="${review.id}"
              ${reviewsState[sectionId]?.visibleReviewIds.includes(review.id) ? 'checked' : ''}
            >
            <label for="review-id-${review.id}" class="customer-reviews__card-select">
              ${review.id}
            </label>
          </div>
        ` : ''}
        ${mediaHTML}
        <div class="customer-reviews__card-content">
          <div class="customer-reviews__reviewer-info">
            ${reviewerImage ? `<img src="${reviewerImage}" alt="${escapeHtml(reviewerName)}" class="customer-reviews__reviewer-avatar">` : '<div class="customer-reviews__reviewer-avatar icon icon-account"></div>'}
            <div class="customer-reviews__reviewer-details">
              <h4 class="customer-reviews__reviewer-name">${escapeHtml(reviewerName)}</h4>
              <p class="customer-reviews__review-date">${reviewDate}</p>
            </div>
          </div>
          <div class="customer-reviews__rating">${stars}</div>
          ${reviewTitle ? `<h5 class="customer-reviews__review-title">${escapeHtml(reviewTitle)}</h5>` : ''}
          <p class="customer-reviews__review-text">${escapeHtml(reviewText)}</p>
        </div>
      </div>
    `;
  }).join('');

  grid.innerHTML = reviewsHTML;
}

/**
 * Apply masonry row spans to each card based on content height
 * @param {string} sectionId - Section ID
 */
function applyRowSpans(sectionId) {
  const grid = document.getElementById(`reviews-grid-${sectionId}`);
  if (!grid) return;

  const gridComputedStyle = window.getComputedStyle(grid);
  const rawRowHeight = gridComputedStyle.getPropertyValue('grid-auto-rows');
  if (rawRowHeight === 'auto' || isNaN(parseInt(rawRowHeight))) return;

  const rowHeight = parseInt(rawRowHeight) || 1;
  const rowGap = parseInt(gridComputedStyle.getPropertyValue('row-gap')) || 0;

  const resizeCards = () => {
    grid.querySelectorAll('.customer-reviews__card').forEach(card => {
      card.style.gridRowEnd = '';
      const marginBottom = parseInt(window.getComputedStyle(card).marginBottom) || 0;
      const cardHeight = card.getBoundingClientRect().height + marginBottom;
      const rowSpan = Math.ceil((cardHeight + rowGap) / (rowHeight + rowGap));
      card.style.gridRowEnd = `span ${rowSpan}`;
    });
  };

  resizeCards(); // synchronous, getBoundingClientRect forces layout

  // Safety net: re-measure if any late image load changes a card's height
  grid.querySelectorAll('img').forEach(img => {
    if (!img.complete) {
      img.addEventListener('load', resizeCards, { once: true });
      img.addEventListener('error', resizeCards, { once: true });
    }
  });
}

function initMediaCarousels(sectionId) {
  const grid = document.getElementById(`reviews-grid-${sectionId}`);
  if (!grid) return;

  grid.querySelectorAll('.customer-reviews__card-media').forEach(media => {
    const slideCount = media.querySelectorAll('.customer-reviews__media-slide').length;
    if (slideCount <= 1) return;

    // Wrap slides in swiper required structure
    const track = media.querySelector('.customer-reviews__media-track');
    if (track) track.classList.add('swiper-wrapper');
    media.querySelectorAll('.customer-reviews__media-slide').forEach(slide => {
      slide.classList.add('swiper-slide');
    });

    new Swiper(media, {
      slidesPerView: 1,
      loop: false,
      lazy: {
        loadPrevNext: true, // preloads next slide
      },
      pagination: {
        el: media.querySelector('.swiper-pagination'),
        clickable: true,
      },
      on: {
        slideChange(swiper) {
          // Pause all videos when sliding
          media.querySelectorAll('video').forEach(v => v.pause());

          // Lazy load video src on approach
          const nextSlide = media.querySelectorAll('.customer-reviews__media-slide')[swiper.activeIndex + 1];
          if (nextSlide) {
            const video = nextSlide.querySelector('video[data-src]');
            if (video) {
                const src = video.getAttribute('data-src');
                if (src) {
                  video.src = src;
                  video.removeAttribute('data-src');
                  video.load();
                }
            }
          }
        }
      }
    });

    media.querySelectorAll('.customer-reviews__video-play').forEach(btn => {
      btn.addEventListener('click', () => {
        const container = btn.closest('.customer-reviews__media-item--video');
        const video = container?.querySelector('video');

        if (!video) {
          console.warn('No video found in container');
          return;
        }

        video.play()
          .then(() => {
            btn.style.display = 'none';
          })
          .catch(err => {
            console.warn('Video play failed:', err);
            btn.style.display = ''; // show button again if play fails
          });
      });
    });

    media.querySelectorAll('video').forEach(video => {
      const container = video.closest('.customer-reviews__media-item--video');
      const btn = container?.querySelector('.customer-reviews__video-play');
      const durationBadge = container?.querySelector('.customer-reviews__media-duration');
      let countdownInterval = null;

      // Store original duration text
      const originalDuration = durationBadge?.textContent;

      video.addEventListener('play', () => {
        if (!durationBadge) return;

        countdownInterval = setInterval(() => {
          const remaining = video.duration - video.currentTime;
          if (!isNaN(remaining) && remaining >= 0) {
            durationBadge.textContent = formatDuration(remaining);
          }
        }, 250);
      });

      video.addEventListener('pause', () => {
        clearInterval(countdownInterval);
        if (btn) btn.style.display = '';
        if (durationBadge && originalDuration) durationBadge.textContent = originalDuration;
      });

      video.addEventListener('ended', () => {
        clearInterval(countdownInterval);
        if (btn) btn.style.display = '';
        if (durationBadge && originalDuration) durationBadge.textContent = originalDuration;
      });
    });

    media.querySelectorAll('video').forEach(video => {
      video.addEventListener('pause', () => {
        const container = video.closest('.customer-reviews__media-item--video');
        const btn = container?.querySelector('.customer-reviews__video-play');
        if (btn) btn.style.display = '';
      });

      video.addEventListener('ended', () => {
        const container = video.closest('.customer-reviews__media-item--video');
        const btn = container?.querySelector('.customer-reviews__video-play');
        if (btn) btn.style.display = '';
      });
    });
  });
}

function initDesignModeCheckboxes(sectionId) {
  const grid = document.getElementById(`reviews-grid-${sectionId}`);
  if (!grid || !window.akc?.designMode) return;

  // Prevent duplicate listeners on re-renders
  if (grid.dataset.designModeInit === 'true') return;
  grid.dataset.designModeInit = 'true';

  // Save button
  const btn = document.getElementById(`reviews-copy-${sectionId}`);
  btn?.addEventListener('click', () => copyReviewIdsToClipboard(sectionId));

  // Checkboxes
  grid.addEventListener('change', e => {
    const checkbox = e.target.closest('.customer-reviews__card-checkbox');
    if (!checkbox) return;

    const reviewId = Number(checkbox.dataset.reviewId);
    const state = reviewsState[sectionId];
    if (!state) return;

    if (checkbox.checked) {
      if (!state.visibleReviewIds.some(id => id === reviewId)) {
        state.visibleReviewIds.push(reviewId);
      }
    } else {
      state.visibleReviewIds = state.visibleReviewIds.filter(id => id !== reviewId);
    }

    updateCopyButton(sectionId);
  });
}

function updateCopyButton(sectionId) {
  const state = reviewsState[sectionId];
  const btn = document.getElementById(`reviews-copy-${sectionId}`);
  if (!btn || !state) return;

  const savedIds = btn.dataset.copyIds ?? '';
  const pending = state.visibleReviewIds.join(', ');
  const isDirty = savedIds !== pending;

  btn.disabled = !isDirty;
  btn.textContent = isDirty ? `Copy (${state.visibleReviewIds.length})` : 'Copied';
}

const copyReviewIdsToClipboard = (sectionId) => {
  const state = reviewsState[sectionId];
  const btn = document.getElementById(`reviews-copy-${sectionId}`);
  if (!state || !btn) return;

  const visibleReviewIdsString = state.visibleReviewIds.join(', ');

  // Success handler to keep code DRY
  const handleSuccess = () => {
    btn.dataset.copyIds = visibleReviewIdsString;
    updateCopyButton(sectionId);
    console.log('Copied successfully!');
  };

  // Try the modern Clipboard API first
  navigator.clipboard.writeText(visibleReviewIdsString)
    .then(handleSuccess)
    .catch(err => {
      console.warn('Modern Clipboard API blocked or failed, attempting fallback...', err);

      // FALLBACK: Old-school textarea trick for restricted iframes
      try {
        const textarea = document.createElement('textarea');
        textarea.value = visibleReviewIdsString;

        // Prevent scrolling down when appending to body
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (successful) {
          handleSuccess();
        } else {
          throw new Error('execCommand returned false');
        }
      } catch (fallbackErr) {
        console.error('Fallback copy method also failed: ', fallbackErr);
      }
    });
};

/**
 * Render pagination controls
 * @param {string} sectionId - Section ID
 */
function renderPagination(sectionId) {
  const state = reviewsState[sectionId];
  if (!state) return;

  const container = document.getElementById(`pagination-numbers-${sectionId}`);
  if (!container) return;

  container.innerHTML = '';

  const totalPages = state.totalPages;
  const currentPage = state.currentPage;
  let pageNumbers = [];

  // Determine which page numbers to show
  if (totalPages <= 5) {
    pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else {
    pageNumbers = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    if (start > 1) {
      pageNumbers.push(1);
      if (start > 2) {
        pageNumbers.push('...');
      }
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPages);
    }
  }

  // Render page number buttons
  pageNumbers.forEach(pageNum => {
    if (pageNum === '...') {
      const ellipsis = document.createElement('span');
      ellipsis.className = 'customer-reviews__pagination-ellipsis';
      ellipsis.textContent = '...';
      container.appendChild(ellipsis);
    } else {
      const button = document.createElement('button');
      button.className = 'customer-reviews__pagination-number';
      button.textContent = pageNum;

      if (pageNum === currentPage) {
        button.classList.add('active');
      }

      button.addEventListener('click', () => {
        goToPage(sectionId, pageNum);
      });

      container.appendChild(button);
    }
  });

  // Update arrow button states
  updateArrowButtons(sectionId);
}

/**
 * Navigate to a specific page
 * @param {string} sectionId - Section ID
 * @param {number} pageNum - Page number
 */
async function goToPage(sectionId, pageNum) {
  const state = reviewsState[sectionId];
  if (!state || state.loading || pageNum < 1 || pageNum > state.totalPages) return;

  sessionStorage.setItem(`akc-reviews-page-${sectionId}`, pageNum);

  scrollToReviews(sectionId);
  await fetchAndDisplayReviews(sectionId, pageNum);
}

/**
 * Update arrow button disabled states
 * @param {string} sectionId - Section ID
 */
function updateArrowButtons(sectionId) {
  const state = reviewsState[sectionId];
  if (!state) return;

  const pagination = document.getElementById(`pagination-${sectionId}`);
  if (!pagination) return;

  const prevBtn = pagination.querySelector('.customer-reviews__pagination-arrow--prev');
  const nextBtn = pagination.querySelector('.customer-reviews__pagination-arrow--next');

  if (prevBtn) {
    prevBtn.disabled = state.currentPage <= 1;
    prevBtn.onclick = () => {
      if (state.currentPage > 1) {
        goToPage(sectionId, state.currentPage - 1);
      }
    };
  }

  if (nextBtn) {
    nextBtn.disabled = state.currentPage >= state.totalPages;
    nextBtn.onclick = () => {
      if (state.currentPage < state.totalPages) {
        goToPage(sectionId, state.currentPage + 1);
      }
    };
  }
}

/**
 * Scroll to reviews section
 * @param {string} sectionId - Section ID
 */
function scrollToReviews(sectionId) {
  const section = document.querySelector(`[data-section-id="${sectionId}"]`);
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Generate star rating HTML
 * @param {number} score - Rating score (0-5)
 * @returns {string} HTML string with stars
 */
function generateStars(score) {
  const fullStars = Math.floor(score);
  const hasHalfStar = score % 1 >= 0.5;
  let starsHTML = '';

  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      starsHTML += '<span class="customer-reviews__star customer-reviews__star--full">★</span>';
    } else if (i === fullStars && hasHalfStar) {
      starsHTML += '<span class="customer-reviews__star customer-reviews__star--half">★</span>';
    } else {
      starsHTML += '<span class="customer-reviews__star customer-reviews__star--empty">☆</span>';
    }
  }

  return starsHTML;
}

/**
 * Format duration in seconds to m:ss
 * @param {number} seconds
 * @returns {string} e.g. "0:18" or "1:04"
 */
function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Format date string
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Escape HTML special characters
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAllCustomerReviewsSections);
} else {
  initializeAllCustomerReviewsSections();
}

// Recalculate row spans on window resize
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    Object.keys(reviewsState).forEach(sectionId => {
      applyRowSpans(sectionId);
    });
  }, 250);
});
